import { UilArrow, UilEdit, UilRobot } from '@iconscout/react-unicons';
import { motion } from 'framer-motion';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const iconVariants = {
  hidden: { opacity: 0, scale: 0, rotate: -180 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      type: 'spring',
      stiffness: 150,
      damping: 12,
      delay: 0.4,
    },
  },
};

const ServicesCard = ({ icon, title, headline, description }) => {
  return (
    <motion.div
      className="flex bg-white flex-col w-full max-w-[280px] xs:max-w-[300px] sm:max-w-[320px] md:max-w-[340px] lg:max-w-[360px] xl:max-w-[400px] min-h-[240px] sm:min-h-[260px] md:min-h-[270px] lg:min-h-[280px] xl:min-h-[310px] rounded-xl shadow-md z-20 border-[1px] p-3 border-[#B7BCC5] hover:cursor-pointer"
      variants={cardVariants}
      whileHover={{
        y: -6,
        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.12)',
        borderColor: '#9ca3af',
      }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <div className="flex flex-row w-full justify-center">
        <div className="w-11 h-10 sm:w-12 sm:h-11 lg:w-14 lg:h-12 xl:w-[60px] xl:h-[55px] -translate-y-7 sm:-translate-y-8 lg:-translate-y-9 shadow-sm rounded-xl bg-[#0D5192] place-items-center flex flex-row justify-center">
          {icon}
        </div>
      </div>

      <motion.div
        className="text-center px-1 text-base sm:text-lg lg:text-xl font-semibold -translate-y-3 sm:-translate-y-4 font-primary pb-1 lg:pb-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        {title}
      </motion.div>

      <motion.div
        className="text-center text-xs sm:text-sm lg:text-base text-[#0D5192] -translate-y-1 sm:-translate-y-2 font-semibold pb-1 sm:pb-2 lg:pb-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        {headline}
      </motion.div>

      <p className="text-center w-full font-semibold px-2 leading-relaxed lg:leading-loose text-gray-500 h-fit text-xs sm:text-sm lg:text-base font-primary">
        {description}
      </p>
    </motion.div>
  );
};

const Services = () => {
  const services = [
   
    {
      id: 1,
      icon: <UilArrow width="32px" height="32px" color="white" />,
      title: 'Product Development',
      headline: 'Make your product vision come to life!',
      description:
        'We build AI and data-driven products and tools across web, desktop, and mobile platforms. Our team delivers development across the stack from prototyping to deployment.',
    },
    {
      id: 2,
      icon: <UilRobot width="32px" height="32px" color="white" />,
      title: 'AI Consulting',
      headline: 'Navigate generative AI correctly with guidance!',
      description:
        'We help organizations navigate the evolving landscape of AI automation with strategies, implementation roadmaps, and up-to-date best practices.',
    },
    {
      id: 3,
      icon: <UilEdit width="30px" height="30px" color="white" />,
      title: 'Technical Education',
      headline: 'Stay ahead with applied learning!',
      description:
        'Follow us through YouTube tutorials, blogs, and product offerings as we help organizations evaluate, refine, and advance their AI adoption and implementation strategies.',
    },
  ];

  return (
    <section
      id="Services"
      className="pt-16 sm:pt-20 md:pt-24 lg:pt-28 min-h-[100vh] flex flex-col items-center"
    >

      <motion.div
        className="text-2xl xs:text-3xl md:text-4xl xl:text-5xl font-primary p-4 sm:p-6 md:p-8 xl:p-10 bg-white pb-8 sm:pb-10 md:pb-12 lg:pb-16 xl:pb-20 z-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      >
        What we do
      </motion.div>

      <motion.div
        className="flex flex-wrap justify-center items-center font-primary gap-3 sm:gap-4 md:gap-5 xl:gap-6 z-20 bg-white p-3 px-4 sm:px-6"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
      >
        {services.map((service) => (
          <ServicesCard
            key={service.id}
            icon={service.icon}
            title={service.title}
            headline={service.headline}
            description={service.description}
          />
        ))}
      </motion.div>
    </section>
  );
};

export default Services;
